import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Alert,
  Paper,
  Tabs,
  Tab,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Badge,
  Switch,
  FormControlLabel,
  Slider,
  Stack,
  Rating,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Analytics as AnalyticsIcon,
  Assessment as AssessmentIcon,
  Compare as CompareIcon,
  Timeline as TimelineIcon,
  ExpandMore as ExpandMoreIcon,
  Settings as SettingsIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  Today as TodayIcon,
  Event as EventIcon,
  Block as BlockIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  LocationOn as LocationIcon,
  Home as HomeIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  Speed as SpeedIcon,
} from '@mui/icons-material';
import MoneyIcon from '@mui/icons-material/AttachMoney';

function TabPanel({
  children, value, index, ...other
}) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`market-tabpanel-${index}`}
      aria-labelledby={`market-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function AdvancedMarketAnalysis({ property, onUpdate }) {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedComparable, setSelectedComparable] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [analysisSettings, setAnalysisSettings] = useState({
    radius: 1,
    timeFrame: '6months',
    includePending: true,
    includeSold: true,
    minPrice: 0,
    maxPrice: 5000000,
  });

  // Mock market data
  const mockMarketData = {
    property: {
      id: property?.id,
      address: '123 Main St, City, State',
      price: 1250000,
      sqft: 2500,
      beds: 3,
      baths: 2.5,
      lotSize: 0.25,
      yearBuilt: 2015,
      daysOnMarket: 45,
    },
    comparables: [
      {
        id: 1,
        address: '125 Main St, City, State',
        price: 1200000,
        soldPrice: 1180000,
        soldDate: new Date('2024-06-15'),
        sqft: 2400,
        beds: 3,
        baths: 2,
        lotSize: 0.22,
        yearBuilt: 2014,
        daysOnMarket: 38,
        distance: 0.1,
        pricePerSqft: 491.67,
        adjustments: {
          sqft: -20000,
          beds: 0,
          baths: -15000,
          lotSize: -5000,
          yearBuilt: 5000,
        },
        adjustedPrice: 1165000,
        status: 'Sold',
        features: ['Updated Kitchen', 'Hardwood Floors', 'Garage'],
      },
      {
        id: 2,
        address: '130 Oak Ave, City, State',
        price: 1350000,
        soldPrice: 1320000,
        soldDate: new Date('2024-06-20'),
        sqft: 2600,
        beds: 4,
        baths: 3,
        lotSize: 0.28,
        yearBuilt: 2016,
        daysOnMarket: 42,
        distance: 0.3,
        pricePerSqft: 507.69,
        adjustments: {
          sqft: 20000,
          beds: 25000,
          baths: 15000,
          lotSize: 5000,
          yearBuilt: 10000,
        },
        adjustedPrice: 1275000,
        status: 'Sold',
        features: ['Pool', 'Updated Kitchen', 'Master Suite'],
      },
      {
        id: 3,
        address: '115 Pine St, City, State',
        price: 1100000,
        soldPrice: null,
        soldDate: null,
        sqft: 2300,
        beds: 3,
        baths: 2,
        lotSize: 0.20,
        yearBuilt: 2013,
        daysOnMarket: 15,
        distance: 0.2,
        pricePerSqft: 478.26,
        adjustments: {
          sqft: -40000,
          beds: 0,
          baths: -15000,
          lotSize: -10000,
          yearBuilt: 0,
        },
        adjustedPrice: 1040000,
        status: 'Active',
        features: ['Updated Bathrooms', 'Fenced Yard'],
      },
    ],
    marketTrends: {
      averagePrice: 1225000,
      averageDaysOnMarket: 42,
      pricePerSqft: 490,
      inventory: 45,
      monthsOfInventory: 3.2,
      priceChange: 2.5,
      daysOnMarketChange: -5,
      inventoryChange: -8,
      marketType: 'Seller\'s Market',
      trend: 'up',
    },
    neighborhood: {
      name: 'Downtown District',
      walkScore: 85,
      transitScore: 78,
      bikeScore: 72,
      schoolRating: 8.5,
      crimeRate: 'Low',
      amenities: ['Restaurants', 'Shopping', 'Parks', 'Schools'],
      demographics: {
        medianAge: 35,
        medianIncome: 85000,
        homeOwnership: 65,
        education: 'High',
      },
    },
    predictions: {
      priceForecast: {
        threeMonths: 1275000,
        sixMonths: 1300000,
        oneYear: 1350000,
        confidence: 85,
      },
      marketTiming: {
        recommendation: 'Good time to sell',
        reasoning: 'Low inventory, high demand, favorable market conditions',
        risk: 'Low',
      },
      daysOnMarket: {
        predicted: 35,
        range: [25, 45],
      },
    },
  };

  const [marketData, setMarketData] = useState(mockMarketData);
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('distance');

  useEffect(() => {
    if (property) {
      setMarketData(mockMarketData);
    }
  }, [property]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleAddComparable = () => {
    setSelectedComparable(null);
    setOpenDialog(true);
  };

  const handleEditComparable = (comparable) => {
    setSelectedComparable(comparable);
    setOpenDialog(true);
  };

  const handleDeleteComparable = (comparableId) => {
    setMarketData((prev) => ({
      ...prev,
      comparables: prev.comparables.filter((c) => c.id !== comparableId),
    }));
  };

  const handleSaveComparable = (comparableData) => {
    if (selectedComparable) {
      setMarketData((prev) => ({
        ...prev,
        comparables: prev.comparables.map((c) => (c.id === selectedComparable.id ? { ...c, ...comparableData } : c)),
      }));
    } else {
      const newComparable = {
        id: Date.now(),
        ...comparableData,
        adjustments: {
          sqft: 0,
          beds: 0,
          baths: 0,
          lotSize: 0,
          yearBuilt: 0,
        },
        adjustedPrice: comparableData.price,
      };
      setMarketData((prev) => ({
        ...prev,
        comparables: [...prev.comparables, newComparable],
      }));
    }
    setOpenDialog(false);
  };

  const getFilteredComparables = () => {
    let filtered = marketData.comparables;

    if (filterStatus !== 'all') {
      filtered = filtered.filter((c) => c.status === filterStatus);
    }

    // Sort by selected criteria
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'distance':
          return a.distance - b.distance;
        case 'price':
          return a.price - b.price;
        case 'date':
          return new Date(b.soldDate || 0) - new Date(a.soldDate || 0);
        case 'adjustedPrice':
          return a.adjustedPrice - b.adjustedPrice;
        default:
          return 0;
      }
    });

    return filtered;
  };

  const calculateAveragePrice = () => {
    const soldComparables = marketData.comparables.filter((c) => c.status === 'Sold');
    if (soldComparables.length === 0) return 0;
    return soldComparables.reduce((sum, c) => sum + c.adjustedPrice, 0) / soldComparables.length;
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);

  const formatDate = (date) => new Date(date).toLocaleDateString();

  const renderComparableSales = () => (
    <Box>
      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2,
      }}
      >
        <Typography variant="h6">
          Comparable Sales (
          {getFilteredComparables().length}
          {' '}
          properties)
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddComparable}
        >
          Add Comparable
        </Button>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="all">All Properties</MenuItem>
                  <MenuItem value="Sold">Sold</MenuItem>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Pending">Pending</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  label="Sort By"
                >
                  <MenuItem value="distance">Distance</MenuItem>
                  <MenuItem value="price">Price</MenuItem>
                  <MenuItem value="date">Date</MenuItem>
                  <MenuItem value="adjustedPrice">Adjusted Price</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button variant="outlined" startIcon={<RefreshIcon />}>
                Refresh Data
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" fontWeight={700} color="primary">
                {formatCurrency(calculateAveragePrice())}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Average Adjusted Price
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" fontWeight={700} color="success.main">
                {marketData.comparables.filter((c) => c.status === 'Sold').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Sold Properties
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" fontWeight={700} color="info.main">
                {marketData.marketTrends.averageDaysOnMarket}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Avg Days on Market
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h4" fontWeight={700} color="warning.main">
                {formatCurrency(marketData.marketTrends.pricePerSqft)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Price per Sq Ft
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Comparables List */}
      <Card>
        <CardContent>
          {getFilteredComparables().length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
              <CompareIcon sx={{ fontSize: 48, mb: 2 }} />
              <Typography variant="h6">No comparable properties found</Typography>
              <Typography variant="body2">Try adjusting your filters or add new comparables</Typography>
            </Box>
          ) : (
            <List>
              {getFilteredComparables().map((comparable, index) => (
                <React.Fragment key={comparable.id}>
                  <ListItem>
                    <ListItemAvatar>
                      <Badge
                        badgeContent={comparable.status === 'Sold' ? '✓' : '●'}
                        color={comparable.status === 'Sold' ? 'success' : 'primary'}
                      >
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          <HomeIcon />
                        </Avatar>
                      </Badge>
                    </ListItemAvatar>
                    <ListItemText
                      primary={(
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {comparable.address}
                          </Typography>
                          <Chip
                            label={comparable.status}
                            color={comparable.status === 'Sold' ? 'success' : 'primary'}
                            size="small"
                          />
                          <Chip
                            label={`${comparable.distance} mi`}
                            variant="outlined"
                            size="small"
                          />
                        </Box>
                      )}
                      secondary={(
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {formatCurrency(comparable.price)}
                            {' '}
                            •
                            {comparable.sqft}
                            {' '}
                            sqft •
                            {comparable.beds}
                            {' '}
                            bed,
                            {comparable.baths}
                            {' '}
                            bath
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {comparable.yearBuilt}
                            {' '}
                            •
                            {comparable.lotSize}
                            {' '}
                            acres •
                            {comparable.daysOnMarket}
                            {' '}
                            days on market
                          </Typography>
                          {comparable.status === 'Sold' && (
                            <Typography variant="body2" color="success.main" fontWeight={600}>
                              Sold:
                              {' '}
                              {formatCurrency(comparable.soldPrice)}
                              {' '}
                              on
                              {' '}
                              {formatDate(comparable.soldDate)}
                            </Typography>
                          )}
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              Adjusted Price:
                              {' '}
                              {formatCurrency(comparable.adjustedPrice)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Price per sqft:
                              {' '}
                              {formatCurrency(comparable.pricePerSqft)}
                            </Typography>
                          </Box>
                          {comparable.features.length > 0 && (
                            <Box sx={{ mt: 1 }}>
                              {comparable.features.map((feature, idx) => (
                                <Chip key={idx} label={feature} size="small" variant="outlined" sx={{ mr: 0.5, mb: 0.5 }} />
                              ))}
                            </Box>
                          )}
                        </Box>
                      )}
                    />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Edit Comparable">
                        <IconButton onClick={() => handleEditComparable(comparable)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Comparable">
                        <IconButton onClick={() => handleDeleteComparable(comparable.id)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </ListItem>
                  {index < getFilteredComparables().length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </CardContent>
      </Card>
    </Box>
  );

  const renderMarketTrends = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Market Trends & Analysis
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Market Overview
              </Typography>
              <List>
                <ListItem>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: marketData.marketTrends.trend === 'up' ? 'success.main' : 'error.main' }}>
                      {marketData.marketTrends.trend === 'up' ? <TrendingUpIcon /> : <TrendingDownIcon />}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={marketData.marketTrends.marketType}
                    secondary={`${marketData.marketTrends.priceChange}% price change, ${marketData.marketTrends.daysOnMarketChange}% DOM change`}
                  />
                </ListItem>
                <ListItem>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      <MoneyIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={formatCurrency(marketData.marketTrends.averagePrice)}
                    secondary="Average Sale Price"
                  />
                </ListItem>
                <ListItem>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'info.main' }}>
                      <SpeedIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={`${marketData.marketTrends.averageDaysOnMarket} days`}
                    secondary="Average Days on Market"
                  />
                </ListItem>
                <ListItem>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'warning.main' }}>
                      <BusinessIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={`${marketData.marketTrends.inventory} properties`}
                    secondary={`${marketData.marketTrends.monthsOfInventory} months of inventory`}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Neighborhood Insights
              </Typography>
              <List>
                <ListItem>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'success.main' }}>
                      <LocationIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={marketData.neighborhood.name}
                    secondary="Neighborhood"
                  />
                </ListItem>
                <ListItem>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      <StarIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={`${marketData.neighborhood.walkScore}/100`}
                    secondary="Walk Score"
                  />
                </ListItem>
                <ListItem>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'info.main' }}>
                      <PeopleIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={`${marketData.neighborhood.schoolRating}/10`}
                    secondary="School Rating"
                  />
                </ListItem>
                <ListItem>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'warning.main' }}>
                      <MoneyIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={formatCurrency(marketData.neighborhood.demographics.medianIncome)}
                    secondary="Median Income"
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  const renderPredictions = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Price Predictions & Market Timing
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Price Forecast
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Confidence Level:
                  {' '}
                  {marketData.predictions.priceForecast.confidence}
                  %
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={marketData.predictions.priceForecast.confidence}
                  sx={{ height: 8, borderRadius: 4, mb: 2 }}
                />
              </Box>
              <List>
                <ListItem>
                  <ListItemText
                    primary="3 Months"
                    secondary={formatCurrency(marketData.predictions.priceForecast.threeMonths)}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="6 Months"
                    secondary={formatCurrency(marketData.predictions.priceForecast.sixMonths)}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="1 Year"
                    secondary={formatCurrency(marketData.predictions.priceForecast.oneYear)}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Market Timing
              </Typography>
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {marketData.predictions.marketTiming.recommendation}
                </Typography>
                <Typography variant="body2">
                  {marketData.predictions.marketTiming.reasoning}
                </Typography>
              </Alert>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Risk Level:
                  {' '}
                  {marketData.predictions.marketTiming.risk}
                </Typography>
                <Chip
                  label={marketData.predictions.marketTiming.risk}
                  color="success"
                  size="small"
                />
              </Box>
              <Typography variant="body2" color="text.secondary">
                Predicted Days on Market:
                {' '}
                {marketData.predictions.daysOnMarket.predicted}
                {' '}
                days
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Range:
                {' '}
                {marketData.predictions.daysOnMarket.range[0]}
                {' '}
                -
                {' '}
                {marketData.predictions.daysOnMarket.range[1]}
                {' '}
                days
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Advanced Market Analysis
      </Typography>

      <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
        <Tab label="Comparable Sales" icon={<CompareIcon />} />
        <Tab label="Market Trends" icon={<TrendingUpIcon />} />
        <Tab label="Predictions" icon={<AnalyticsIcon />} />
      </Tabs>

      <TabPanel value={activeTab} index={0}>
        {renderComparableSales()}
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        {renderMarketTrends()}
      </TabPanel>

      <TabPanel value={activeTab} index={2}>
        {renderPredictions()}
      </TabPanel>

      {/* Add/Edit Comparable Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedComparable ? 'Edit Comparable' : 'Add New Comparable'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="Address *"
                value={selectedComparable?.address || ''}
                onChange={(e) => setSelectedComparable((prev) => ({
                  ...prev,
                  address: e.target.value,
                }))}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Price *"
                value={selectedComparable?.price || ''}
                onChange={(e) => setSelectedComparable((prev) => ({
                  ...prev,
                  price: parseFloat(e.target.value) || 0,
                }))}
                fullWidth
                type="number"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={selectedComparable?.status || 'Active'}
                  onChange={(e) => setSelectedComparable((prev) => ({
                    ...prev,
                    status: e.target.value,
                  }))}
                  label="Status"
                >
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Sold">Sold</MenuItem>
                  <MenuItem value="Pending">Pending</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Square Feet"
                value={selectedComparable?.sqft || ''}
                onChange={(e) => setSelectedComparable((prev) => ({
                  ...prev,
                  sqft: parseFloat(e.target.value) || 0,
                }))}
                fullWidth
                type="number"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Distance (miles)"
                value={selectedComparable?.distance || ''}
                onChange={(e) => setSelectedComparable((prev) => ({
                  ...prev,
                  distance: parseFloat(e.target.value) || 0,
                }))}
                fullWidth
                type="number"
                step="0.1"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Bedrooms"
                value={selectedComparable?.beds || ''}
                onChange={(e) => setSelectedComparable((prev) => ({
                  ...prev,
                  beds: parseInt(e.target.value) || 0,
                }))}
                fullWidth
                type="number"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Bathrooms"
                value={selectedComparable?.baths || ''}
                onChange={(e) => setSelectedComparable((prev) => ({
                  ...prev,
                  baths: parseFloat(e.target.value) || 0,
                }))}
                fullWidth
                type="number"
                step="0.5"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Year Built"
                value={selectedComparable?.yearBuilt || ''}
                onChange={(e) => setSelectedComparable((prev) => ({
                  ...prev,
                  yearBuilt: parseInt(e.target.value) || 0,
                }))}
                fullWidth
                type="number"
              />
            </Grid>
            {selectedComparable?.status === 'Sold' && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Sold Price"
                    value={selectedComparable?.soldPrice || ''}
                    onChange={(e) => setSelectedComparable((prev) => ({
                      ...prev,
                      soldPrice: parseFloat(e.target.value) || 0,
                    }))}
                    fullWidth
                    type="number"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Sold Date"
                    value={selectedComparable?.soldDate || ''}
                    onChange={(e) => setSelectedComparable((prev) => ({
                      ...prev,
                      soldDate: e.target.value,
                    }))}
                    fullWidth
                    type="date"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            onClick={() => handleSaveComparable(selectedComparable)}
            variant="contained"
            disabled={!selectedComparable?.address || !selectedComparable?.price}
          >
            {selectedComparable?.id ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AdvancedMarketAnalysis;
